import { useEffect, useLayoutEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import cx from 'classnames';

import useDelayedUnmount from '../../../hooks/useDelayedUnmount';

// The gap the menu keeps from its trigger, and from the edge of the window when it is pushed
// against one. Matches the `top: calc(100% + 6px)` the anchored menu uses.
const MENU_GAP = 6;

const Dropdown = ({
  trigger,
  align = 'right',
  className,
  triggerClassName,
  menuClassName,
  children,
  onOpenChange,
  disabled = false,
  title,
  ariaLabel,
  // Keeps focus (and so the visible text selection) where it is when the trigger is clicked,
  // for menus whose actions operate on the current selection
  preserveFocus = false,
  // Renders the menu into the body, fixed to the trigger, instead of absolutely inside it. For a
  // menu whose trigger sits in a container that clips or scrolls — a modal body, an overflow:auto
  // list — where an anchored menu is cut off, and the row nearest the edge is exactly the one
  // that loses its items.
  portal = false,
  // Lays the items out in this many columns instead of one. For a menu that is a *scale* rather
  // than a list of actions — every item the same shape, and enough of them that one column becomes
  // a tall strip you have to scroll to reach the end of. Halving the rows usually means the whole
  // thing fits on screen, which is the difference between picking a value and hunting for it.
  columns = 1,
  // Controlled when passed, so a menu can be opened from outside its trigger
  open: controlledOpen,
}) => {
  const [openState, setOpenState] = useState(false);
  const isControlled = controlledOpen !== undefined;
  const open = isControlled ? controlledOpen : openState;
  // The menu outlives `open` by the length of its closing animation, so it can play one
  const mounted = useDelayedUnmount(open);
  const [menuStyle, setMenuStyle] = useState(null);
  // Whether placement had to flip the menu above its trigger, so it can rise from that side
  const [above, setAbove] = useState(false);
  const ref = useRef(null);
  const menuRef = useRef(null);

  const setOpen = (value) => {
    if (isControlled) {
      const next = typeof value === 'function' ? value(controlledOpen) : value;
      if (next !== controlledOpen) onOpenChange?.(next);
      return;
    }

    setOpenState((prev) => {
      const next = typeof value === 'function' ? value(prev) : value;
      if (next !== prev) onOpenChange?.(next);
      return next;
    });
  };

  // The trigger can become disabled while the menu is open (its actions stopped being available)
  useEffect(() => {
    if (disabled) setOpen(false);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [disabled]);

  // Measured and placed before the browser paints, so a portalled menu never shows at the
  // top-left corner it is first rendered into. It stays put while open by following the trigger:
  // scrolling the list underneath it moves both together.
  useLayoutEffect(() => {
    if (!portal || !mounted) {
      setMenuStyle(null);

      return undefined;
    }

    const place = () => {
      const anchor = ref.current?.getBoundingClientRect();
      const menu = menuRef.current;

      if (!anchor || !menu) return;

      const { offsetHeight: height, offsetWidth: width } = menu;
      const below = window.innerHeight - anchor.bottom;
      // Flips above the trigger only when there is more room up there — against a short window
      // it would otherwise trade one clipped edge for the other
      const flip = below < height + MENU_GAP && anchor.top > below;
      const left = align === 'left' ? anchor.left : anchor.right - width;

      setAbove(flip);
      setMenuStyle({
        top: flip ? Math.max(MENU_GAP, anchor.top - height - MENU_GAP) : anchor.bottom + MENU_GAP,
        left: Math.min(Math.max(MENU_GAP, left), Math.max(MENU_GAP, window.innerWidth - width - MENU_GAP)),
      });
    };

    place();

    window.addEventListener('resize', place);
    // Captured, so a scroll of any container between the trigger and the window is heard too
    window.addEventListener('scroll', place, true);

    return () => {
      window.removeEventListener('resize', place);
      window.removeEventListener('scroll', place, true);
    };
  }, [portal, mounted, align]);

  useEffect(() => {
    if (!open) return undefined;

    const isInside = (target) => ref.current?.contains(target) || menuRef.current?.contains(target);

    const handlePointer = (e) => {
      if (!isInside(e.target)) setOpen(false);
    };
    const handleKey = (e) => {
      if (e.key === 'Escape') {
        setOpen(false);
        return;
      }

      if (e.metaKey || e.ctrlKey || e.altKey) return;

      // Don't hijack keystrokes while the user is typing in a field (the menu may contain inputs)
      const { tagName, isContentEditable } = e.target;
      if (isContentEditable || tagName === 'INPUT' || tagName === 'TEXTAREA' || tagName === 'SELECT') return;

      const selector = `[data-shortcut="${e.key.toLowerCase()}"]`;
      // The menu is a sibling of the trigger once portalled, so both are searched
      const shortcutItem = ref.current?.querySelector(selector) || menuRef.current?.querySelector(selector);

      if (shortcutItem) {
        e.preventDefault();
        shortcutItem.click();
      }
    };

    document.addEventListener('mousedown', handlePointer);
    document.addEventListener('touchstart', handlePointer);
    document.addEventListener('keydown', handleKey);
    return () => {
      document.removeEventListener('mousedown', handlePointer);
      document.removeEventListener('touchstart', handlePointer);
      document.removeEventListener('keydown', handleKey);
    };
  // eslint-disable-next-line react-hooks/exhaustive-deps -- setOpen is new each render; the listeners only need resetting when open changes
  }, [open]);

  const handleMenuClick = (e) => {
    if (e.target.closest('.mg-dropdown__item')) setOpen(false);
  };

  const menu = (
    <div
      ref={menuRef}
      className={cx(
        'mg-dropdown__menu',
        portal ? 'mg-dropdown__menu--portal' : `mg-dropdown__menu--${align}`,
        columns > 1 && 'mg-dropdown__menu--columns',
        above && 'mg-dropdown__menu--above',
        !open && 'mg-dropdown__menu--closing',
        menuClassName,
      )}
      // Hidden rather than unrendered until placed: the menu has to be in the document to be
      // measured, and useLayoutEffect places it before the frame is painted
      style={{
        ...(portal ? menuStyle || { top: 0, left: 0, visibility: 'hidden' } : null),
        ...(columns > 1 ? { '--mg-dropdown-columns': columns } : null),
      }}
      role="menu"
      onClick={handleMenuClick}
    >
      {children}
    </div>
  );

  return (
    <div className={cx('mg-dropdown', className)} ref={ref}>
      <button
        type="button"
        className={cx('mg-dropdown__trigger', triggerClassName)}
        aria-haspopup="menu"
        aria-expanded={open}
        aria-label={ariaLabel}
        title={title}
        disabled={disabled}
        onMouseDown={preserveFocus ? (e) => e.preventDefault() : undefined}
        onClick={() => setOpen((o) => !o)}
      >
        {trigger}
      </button>
      {mounted && (portal ? createPortal(menu, document.body) : menu)}
    </div>
  );
};

/**
 * One choice in a menu.
 *
 * `subtitle` puts a second line under the label, for a menu whose items need saying twice — a length
 * named as a fraction of a bar and again in the beats it is worth, say, where the name is what you
 * pick by and the beats are what you count in. It only wraps when there is one, so every item
 * without a subtitle keeps exactly the flat row it has always rendered and the stylesheets that
 * reach into these items go on matching.
 *
 * `trailing` is what has to sit beside that pair rather than inside it — a tick on the chosen one,
 * most of the time. Passing it as a child would put it in the label, where it lands between the two
 * lines instead of to the right of both.
 */
export const DropdownItem = ({ shortcut, subtitle, trailing, className, children, ...props }) => (
  <button type="button" className={cx('mg-dropdown__item', className)} data-shortcut={shortcut} {...props}>
    {shortcut && <span className="mg-dropdown__shortcut">{shortcut.toUpperCase()}</span>}
    {subtitle ? (
      <span className="mg-dropdown__stack">
        <span className="mg-dropdown__label">{children}</span>
        <span className="mg-dropdown__subtitle">{subtitle}</span>
      </span>
    ) : (
      children
    )}
    {trailing}
  </button>
);

export const DropdownDivider = () => <div className="mg-dropdown__divider" />;

export default Dropdown;
